export function capitalizeFirstLetter(string) {
  return string && string.charAt(0).toUpperCase() + string.slice(1);
}

export function capitalizeFirstLetterOtherSmall(string) {
  return (
    string && string.charAt(0).toUpperCase() + string.slice(1).toLowerCase()
  );
}

export function dataURLtoFile(dataurl, filename) {
  // console.log(dataurl);
  var arr = dataurl.split(",");
  let mime = arr[0].match(/:(.*?);/)[1];
  var bstr = window.atob(arr[1]);
  let n = bstr.length;
  let u8arr = new Uint8Array(n);

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  console.log("inside data URL");

  var textFileAsBlob = new Blob([u8arr], { type: mime });
  try {
    return new File([u8arr], filename, { type: mime });
  } catch (err) {
    return window.navigator.msSaveBlob(textFileAsBlob, filename);
  }
}

export const randomId = function(length = 6) {
  return Math.random()
    .toString(36)
    .substring(2, length + 2);
};

/**
 * Rounds all decimal percentages in a text string to whole numbers
 * @param {string} text - Text containing percentages (e.g., "45.67% of users")
 * @returns {string} - Text with rounded percentages (e.g., "46% of users")
 * @example
 * roundPercentagesInText("Sales increased by 23.456%") // "Sales increased by 23%"
 * roundPercentagesInText("Growth: 45.8%, Retention: 89.2%") // "Growth: 46%, Retention: 89%"
 */
export const roundPercentagesInText = (text) => {
  if (!text) return text;
  // Match percentages in format: number.decimal%
  // Replace decimal percentages with rounded whole numbers
  return text.replace(/(\d+\.\d+)%/g, (match, number) => {
    return Math.round(parseFloat(number)) + '%';
  });
};

/**
 * Removes paragraph tags from HTML string while keeping the content
 * @param {string} htmlString - HTML string that may contain <p> tags
 * @returns {string} - String with <p> tags removed
 * @example
 * stripParagraphTags("<p>Hello</p>") // "Hello"
 * stripParagraphTags("<p>Option A</p>") // "Option A"
 */
export const stripParagraphTags = (htmlString) => {
  if (!htmlString) return "";
  // Remove opening and closing p tags, keeping the content
  return htmlString.replace(/<\/?p[^>]*>/g, "");
};

/**
 * Formats a date string into a relative time ago
 * @param {string} dateString
 * @returns {string} - "Just now", "19m ago", "19h ago", "Yesterday", "3d ago" etc.
 */
export const formatTimeAgo = (dateString) => {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return "Yesterday";
  return `${diffDays}d ago`;
};

/**
 * Strips a leading markdown "# Summary" or plain "Summary" heading from API-returned
 * summary text so it doesn't duplicate the modal title.
 * @param {string} text - Raw summary text from the API
 * @returns {string} - Summary text without the redundant heading
 * @example
 * cleanSummaryText("# Summary\nSome content") // "Some content"
 * cleanSummaryText("Summary\nSome content")   // "Some content"
 */
export const cleanSummaryText = (text) => {
  if (!text) return text;
  return text.replace(/^#?\s*summary\s*\n?/i, '').trimStart();
};

/**
 * Builds the display name for a user row in the Share window:
 *   1. firstName + lastName, single space, each trimmed (no comma).
 *   2. If only one is present, just that one.
 *   3. If neither, a real unique identifier - for PUBLIC-link Trivvys the
 *      Admin-configured one first, then the user's own by priority
 *      (Employee ID -> Mobile -> Email), skipping synthetic dummy emails.
 *   4. If nothing real exists, "--".
 * Explicit null checks only (no optional chaining / nullish coalescing).
 * @param {object} row - share row (firstName, lastName, email, employeeId, mobileNumber)
 * @param {object} survey - the Trivvy, for privacy + userIdentificationOptions
 * @returns {string}
 */
export const getShareUserDisplayName = (row, survey) => {
  if (!row) return "--";

  const first = row.firstName ? String(row.firstName).trim() : "";
  const last = row.lastName ? String(row.lastName).trim() : "";
  const name = [first, last].filter(Boolean).join(" ");
  if (name) return name;

  // No name available: revert to the Admin's required unique identifier.
  const opt =
    survey &&
    survey.userIdentificationOptions &&
    survey.userIdentificationOptions[0]
      ? survey.userIdentificationOptions[0]
      : null;

  // userIdentificationOptions[0] is stored as the option VALUE string (e.g.
  // "EMPLOYEE_ID") — the same shape "Results > By User" resolves. Map it to the
  // user field; tolerate a legacy object shape defensively. Mirrors
  // TRIVIA_USER_IDENTIFICATION_OPTIONS in pages/trivia/data.js.
  const valueToField = {
    EMAIL: "email",
    EMPLOYEE_ID: "employeeId",
    MOBILE_NUMBER: "mobileNumber",
  };
  let optValue = "";
  if (typeof opt === "string") {
    optValue = opt;
  } else if (opt && opt.value) {
    optValue = opt.value;
  }
  let configuredField = "";
  if (optValue && valueToField[optValue]) {
    configuredField = valueToField[optValue];
  } else if (opt && opt.field) {
    configuredField = opt.field;
  }

  const candidates = [];
  // PUBLIC-link Trivvys let the Admin pick the identifier ("User Must Provide");
  // honor it first there. For platform sharing that setting is the unreachable
  // EMAIL default, so fall through to the user's own identifiers by priority.
  if (survey && survey.privacy === "PUBLIC" && configuredField) {
    candidates.push(row[configuredField]);
  }
  candidates.push(row.employeeId, row.mobileNumber, row.email);

  for (let i = 0; i < candidates.length; i++) {
    const val = candidates[i] ? String(candidates[i]).trim() : "";
    // Skip the synthetic "temp_...@exampleemail.com" placeholder the BE assigns
    // to blank-email uploads; it is not a real identifier.
    if (val && !/@exampleemail\.com$/i.test(val)) return val;
  }

  return "--";
};
